/**
 * Zod validation schemas for DynamoDB entities
 * Using Zod v4 top-level string formats for optimal performance
 */

import { matchProjectionSchema } from "sams-provider-events";
import { z } from "zod";

/** Base fields for all entities */
export const baseEntityFields = {
  id: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
} as const;

/** Scope granted to a CMS API key. New areas are added here and chosen only when creating a key. */
export const apiKeyScopeSchema = z.enum(["news", "events", "volunteerEvents", "bus"]);

/** One or more scopes on a key. Legacy single-string values are coerced to a one-element array. */
export const apiKeyScopesSchema = z.preprocess((value) => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return [...new Set(value)];
  return value;
}, z.array(apiKeyScopeSchema).min(1));

/** Hashed CMS API key. The bearer token itself is never stored. */
export const apiKeySchema = z.object({
  id: z.uuid(),
  type: z.literal("apiKey").default("apiKey").describe("Entity type discriminator"),
  label: z.string().trim().min(1).max(100),
  memberId: z.uuid(),
  scope: apiKeyScopesSchema,
  secretHash: z
    .string()
    .regex(/^scrypt\$[A-Za-z0-9_-]+\$[A-Za-z0-9_-]+$/)
    .describe("scrypt hash with per-key salt (format scrypt$<salt>$<derived>)"),
  prefix: z.string().min(1).max(16),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().optional(),
});

export const publicApiKeySchema = apiKeySchema.omit({ secretHash: true });

export const createApiKeyInputSchema = z.object({
  label: z.string().trim().min(1).max(100),
  memberId: z.uuid(),
  scope: apiKeyScopesSchema,
});

/** News article schema */
export const newsSchema = z.object({
  ...baseEntityFields,
  type: z.literal("article").describe("Entity type, primary key for GSI queries"),
  title: z.string().min(1).max(200),
  slug: z.string().min(1).max(200),
  content: z.string().min(1).describe("Tiptap rich text content in HTML format"),
  excerpt: z.string().max(500).optional(),
  status: z.enum(["draft", "published", "archived"]),
  imageS3Keys: z.array(z.string()).optional().describe("Array of S3 keys for image gallery"),
  tags: z.array(z.string()).optional(),
  sharedToMastodon: z
    .boolean()
    .optional()
    .describe("Whether this news article has been shared to Mastodon"),
});

/** Event schema */
export const eventSchema = z.object({
  ...baseEntityFields,
  type: z.literal("event").describe("Entity type, primary key for GSI queries"),
  title: z.string().min(1).max(200),
  description: z.string().optional(),
  startDate: z.iso.datetime(),
  endDate: z.iso.datetime().optional(),
  location: z.string().optional(),
  variant: z.string().optional(),
  teamIds: z.array(z.uuid()).optional().describe("Associated team IDs, to filter events by teams"),
  ttl: z.number().int().positive().optional().describe("Unix timestamp for DynamoDB TTL"),
});

/** Training schedule schema */
export const trainingScheduleSchema = z.object({
  days: z
    .array(z.number().int().min(0).max(6))
    .describe("0 = Sunday, 1 = Monday, ..., 6 = Saturday (matches dayjs)"),
  startTime: z
    .string()
    .regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)
    .describe("HH:MM format"),
  endTime: z
    .string()
    .regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)
    .describe("HH:MM format"),
  locationId: z.uuid(),
});

/** Team schema */
export const teamSchema = z.object({
  ...baseEntityFields,
  type: z.literal("team").describe("Entity type, primary key for GSI queries"),
  name: z.string().min(1).max(100),
  slug: z.string().min(1).max(100),
  description: z.string().optional(),
  sbvvTeamId: z.string().optional(),
  ageGroup: z.string().optional(),
  gender: z.enum(["male", "female", "mixed"]),
  league: z.string().optional(),
  trainerIds: z.array(z.string()).optional(),
  pointOfContactIds: z.array(z.string()).optional(),
  pictureS3Keys: z.array(z.string()).optional(),
  trainingSchedules: z.array(trainingScheduleSchema).optional(),
});

/** Member schema */
export const memberSchema = z.object({
  ...baseEntityFields,
  type: z.literal("member").default("member").describe("Entity type, primary key for GSI queries"),
  name: z.string().min(1).max(200),
  privateEmail: z
    .email()
    .trim()
    .optional()
    .describe("Admin-only private destination email — never exposed in public responses"),
  proxyEmail: z
    .email()
    .trim()
    .optional()
    .describe("Public proxy alias — used in all public mailto flows"),
  phone: z.string().optional(),
  isBoardMember: z.boolean().optional(),
  isTrainer: z.boolean().optional(),
  roleTitle: z.string().max(100).optional(),
  avatarS3Key: z.string().optional(),
  authRole: z
    .enum(["Admin", "Moderator"])
    .optional()
    .describe("CMS admin role — when set, this member may log in to the admin panel"),
});

/** Media schema */
export const mediaSchema = z.object({
  ...baseEntityFields,
  filename: z.string().min(1),
  mimeType: z.string().regex(/^[a-z]+\/[a-z0-9\-+.]+$/),
  url: z.url(),
  s3Key: z.string().min(1),
  s3Bucket: z.string().min(1),
  alt: z.string().optional(),
  caption: z.string().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  fileSize: z.number().int().positive(),
  uploadedBy: z.string(),
});

/** Sponsor schema */
export const sponsorSchema = z.object({
  ...baseEntityFields,
  type: z
    .literal("sponsor")
    .default("sponsor")
    .describe("Entity type, primary key for GSI queries"),
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  websiteUrl: z.url().optional(),
  logoS3Key: z.string().optional(),
  ttl: z.number().int().positive().optional().describe("Unix timestamp for DynamoDB TTL"),
});

/** Location schema */
export const locationSchema = z.object({
  ...baseEntityFields,
  type: z
    .literal("location")
    .default("location")
    .describe("Entity type, primary key for GSI queries"),
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  street: z.string().min(1).max(200),
  postal: z.string().min(1).max(20),
  city: z.string().min(1).max(100),
});

/** Bus Booking schema */
export const busSchema = z.object({
  id: z.uuid(),
  type: z.literal("bus").default("bus").describe("Entity type, primary key for GSI queries"),
  driver: z.string().min(1),
  comment: z.string().optional(),
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  ttl: z.number().int().positive().describe("Unix timestamp for TTL"),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** Export types inferred from schemas */
export type NewsInput = z.infer<typeof newsSchema>;
export type EventInput = z.infer<typeof eventSchema>;
export type TeamInput = z.infer<typeof teamSchema>;
export type MemberInput = z.infer<typeof memberSchema>;
export type MediaInput = z.infer<typeof mediaSchema>;
export type SponsorInput = z.infer<typeof sponsorSchema>;
export type LocationInput = z.infer<typeof locationSchema>;
export type BusInput = z.infer<typeof busSchema>;
export type TrainingScheduleInput = z.infer<typeof trainingScheduleSchema>;

// ---------------------------------------------------------------------------
// SAMS entity schemas
// ---------------------------------------------------------------------------

/** SAMS club record (from provider `sams.club.updated` events) */
export const samsClubSchema = z.object({
  sportsclubUuid: z.string().min(1),
  type: z.literal("club").default("club").describe("Entity type discriminator for GSI queries"),
  name: z.string().min(1),
  nameSlug: z.string().min(1).describe("URL-safe slug for case-insensitive queries"),
  associationUuid: z.string().optional(),
  associationName: z.string().optional(),
  logoImageLink: z.string().optional(),
  logoS3Key: z.string().optional(),
  snapshotVersion: z.string().min(1).optional(),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive().describe("Unix timestamp for DynamoDB TTL (1-year expiry)"),
});

/** SAMS team record (from provider club-season team events) */
export const samsTeamSchema = z.object({
  uuid: z.string().min(1),
  type: z.literal("team").default("team").describe("Entity type discriminator for GSI queries"),
  name: z.string().min(1),
  nameSlug: z.string().min(1).describe("URL-safe slug for case-insensitive queries"),
  sportsclubUuid: z.string().min(1),
  associationUuid: z.string().min(1),
  leagueUuid: z.string().min(1),
  leagueName: z.string().min(1),
  leagueHierarchyLevel: z.number().nonnegative().optional(),
  seasonUuid: z.string().min(1),
  seasonName: z.string().min(1),
  snapshotVersion: z.string().min(1).optional(),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive().describe("Unix timestamp for DynamoDB TTL (1-year expiry)"),
});

export type SamsClubInput = z.infer<typeof samsClubSchema>;
export type SamsTeamInput = z.infer<typeof samsTeamSchema>;

/** Sportsclub logo index — CDN URL keyed by sportsclubUuid (from ranking sync). */
export const samsClubLogoSchema = z.object({
  sportsclubUuid: z.string().min(1),
  type: z
    .literal("samslogo")
    .default("samslogo")
    .describe("Entity type discriminator for logo index rows"),
  logoUrl: z.string().url().describe("HTTPS CDN logo URL from ranking entries"),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive().describe("Unix timestamp for DynamoDB TTL (1-year expiry)"),
});

export type SamsClubLogoInput = z.infer<typeof samsClubLogoSchema>;

/** Team logo index — CDN URL keyed by SAMS team UUID (from ranking sync). */
export const samsTeamLogoSchema = z.object({
  teamUuid: z.string().min(1),
  type: z
    .literal("samsteamlogo")
    .default("samsteamlogo")
    .describe("Entity type discriminator for team logo index rows"),
  logoUrl: z.string().min(1).describe("HTTPS CDN or data URI logo URL from ranking entries"),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive().describe("Unix timestamp for DynamoDB TTL (1-year expiry)"),
});

export type SamsTeamLogoInput = z.infer<typeof samsTeamLogoSchema>;

/** Public club record — omits query/TTL internals. */
export const ClubResponseSchema = samsClubSchema.omit({
  nameSlug: true,
  ttl: true,
});
export type ClubResponse = z.infer<typeof ClubResponseSchema>;

/** Public team record — omits query/TTL internals. */
export const TeamResponseSchema = samsTeamSchema.omit({
  nameSlug: true,
  ttl: true,
});
export type TeamResponse = z.infer<typeof TeamResponseSchema>;

/**
 * A player entry within a SAMS team roster
 * uuid and name are always present: sams-teams-sync.ts derives a deterministic pseudo uuid when the
 * external API omits one, and filters out players without a name.
 */
export const samsRosterPlayerSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  jerseyNumber: z.number().optional(),
  position: z.string().optional(),
  portraitImageLink: z.string().optional(),
});

/**
 * An official/coach entry within a SAMS team roster
 * uuid and name are always present: sams-teams-sync.ts derives a deterministic pseudo uuid when the
 * external API omits one, and filters out officials without a name.
 */
export const samsRosterOfficialSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  role: z.string().optional(),
});

/** SAMS team roster record — players + officials for the current season (synced from external SAMS API) */
export const samsRosterSchema = z.object({
  teamUuid: z.string().min(1),
  type: z.literal("roster").default("roster").describe("Entity type discriminator for GSI queries"),
  players: z.array(samsRosterPlayerSchema).default([]),
  officials: z.array(samsRosterOfficialSchema).default([]),
  snapshotVersion: z.string().min(1).optional(),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive().describe("Unix timestamp for DynamoDB TTL (1-year expiry)"),
});

export type SamsRosterInput = z.infer<typeof samsRosterSchema>;
export type SamsRosterPlayerInput = z.infer<typeof samsRosterPlayerSchema>;
export type SamsRosterOfficialInput = z.infer<typeof samsRosterOfficialSchema>;

export const RosterResponseSchema = samsRosterSchema.omit({ ttl: true });
export type RosterResponse = z.infer<typeof RosterResponseSchema>;

/** Stored league match — provider `Match` projection, not a HAL DTO. */
export const samsProjectionMatchSchema = matchProjectionSchema;
export type SamsProjectionMatchInput = z.infer<typeof samsProjectionMatchSchema>;

/** Club schedule projection — rolling match window for one club/season. */
export const samsClubScheduleProjectionSchema = z.object({
  sportsclubUuid: z.string().min(1),
  seasonUuid: z.string().min(1),
  seasonName: z.string().optional(),
  type: z.literal("schedule").default("schedule"),
  matches: z.array(samsProjectionMatchSchema).default([]),
  snapshotVersion: z.string().min(1),
  projectedAt: z.iso.datetime().optional(),
  cachedAt: z.iso.datetime().optional(),
  isStale: z.boolean().optional(),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive(),
});

export type SamsClubScheduleProjectionInput = z.infer<typeof samsClubScheduleProjectionSchema>;

/** League ranking projection row (RankingResponse teams entry shape). */
export const samsProjectionRankingEntrySchema = z.object({
  uuid: z.string(),
  teamName: z.string(),
  rank: z.number().optional(),
  sportsclubUuid: z.string().min(1).optional(),
  logoUrl: z.string().optional(),
  matchesPlayed: z.number().nullish(),
  points: z.number().nullish(),
  wins: z.number().nullish(),
  setWins: z.number().nullish(),
  setLosses: z.number().nullish(),
});

export type SamsProjectionRankingEntryInput = z.infer<typeof samsProjectionRankingEntrySchema>;

/** League ranking projection for one league/season. */
export const samsLeagueRankingProjectionSchema = z.object({
  leagueUuid: z.string().min(1),
  seasonUuid: z.string().min(1),
  seasonName: z.string().optional(),
  leagueName: z.string().optional(),
  type: z.literal("ranking").default("ranking"),
  teams: z.array(samsProjectionRankingEntrySchema).default([]),
  snapshotVersion: z.string().min(1),
  cachedAt: z.iso.datetime().optional(),
  isStale: z.boolean().optional(),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive(),
});

export type SamsLeagueRankingProjectionInput = z.infer<typeof samsLeagueRankingProjectionSchema>;

// ---------------------------------------------------------------------------
// Application read models (Tabelle / Termine) — written at SAMS sync time
// ---------------------------------------------------------------------------

/** Stable dataset id for the currently published application projections. */
export const APP_DATASET_CURRENT = "current" as const;

/** One league standings row in the Tabelle application projection. */
export const appTabelleLeagueSchema = z.object({
  datasetId: z.string().min(1),
  leagueSortKey: z.string().min(1),
  type: z.literal("apptabelle").default("apptabelle"),
  leagueUuid: z.string().min(1),
  leagueName: z.string().min(1),
  leagueHierarchyLevel: z.number().nonnegative().optional(),
  seasonUuid: z.string().min(1),
  seasonName: z.string().optional(),
  teams: z.array(samsProjectionRankingEntrySchema).default([]),
  ownedTeamUuids: z.array(z.string().min(1)).default([]),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive(),
});

export type AppTabelleLeagueInput = z.infer<typeof appTabelleLeagueSchema>;

/** One match row in the Termine application projection. */
export const appTermineMatchSchema = z.object({
  datasetId: z.string().min(1),
  matchSortKey: z.string().min(1),
  type: z.literal("apptermine").default("apptermine"),
  matchUuid: z.string().min(1),
  date: z.string().optional(),
  time: z.string().optional(),
  leagueUuid: z.string().min(1).optional(),
  leagueName: z.string().min(1).optional(),
  seasonUuid: z.string().min(1).optional(),
  team1: samsProjectionMatchSchema.shape.team1,
  team2: samsProjectionMatchSchema.shape.team2,
  location: samsProjectionMatchSchema.shape.location,
  result: samsProjectionMatchSchema.shape.result,
  hasResult: z.boolean(),
  isHomeGame: z.boolean(),
  ownedTeamUuids: z.array(z.string().min(1)).default([]),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive(),
});

export type AppTermineMatchInput = z.infer<typeof appTermineMatchSchema>;

/** Sort key for the single Heimspiele card document inside a dataset partition. */
export const HEIMSPIELE_DOCUMENT_KEY = "META#heimspiele" as const;

/** One future home game, shaped for the homepage card. The 14-day window is applied at read time. */
export const heimspielCardSchema = z.object({
  matchUuid: z.string().min(1),
  date: z.string().optional(),
  time: z.string().optional(),
  leagueUuid: z.string().min(1).optional(),
  leagueName: z.string().min(1).optional(),
  team1Uuid: z.string().min(1),
  team2Uuid: z.string().min(1),
  opponentName: z.string().min(1),
  locationName: z.string().optional(),
  locationUuid: z.string().optional(),
});

export type HeimspielCard = z.infer<typeof heimspielCardSchema>;

export const appHeimspieleSchema = z.object({
  datasetId: z.string().min(1),
  documentKey: z.literal(HEIMSPIELE_DOCUMENT_KEY).default(HEIMSPIELE_DOCUMENT_KEY),
  type: z.literal("appheimspiele").default("appheimspiele"),
  games: z.array(heimspielCardSchema).default([]),
  updatedAt: z.iso.datetime(),
  ttl: z.number().int().positive(),
});

export type AppHeimspieleInput = z.infer<typeof appHeimspieleSchema>;

/** Content-table section keys for homepage read documents. */
export const publicSnapshotSectionSchema = z.enum(["events", "news", "sponsors", "members"]);

export type PublicSnapshotSection = z.infer<typeof publicSnapshotSectionSchema>;

export const publicSnapshotSchema = z.object({
  section: publicSnapshotSectionSchema,
  type: z.literal("publicSnapshot").default("publicSnapshot"),
  payload: z.unknown(),
  updatedAt: z.iso.datetime(),
});

export type PublicSnapshotInput = z.infer<typeof publicSnapshotSchema>;

// ---------------------------------------------------------------------------
// Volunteer Event Planner schemas
// ---------------------------------------------------------------------------

/** Job role within a volunteer shift */
export const volunteerRoleSchema = z
  .object({
    id: z.uuid(),
    label: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    minCapacity: z.number().int().min(0),
    maxCapacity: z.number().int().min(1).optional(),
    minAge: z.number().int().min(0).max(120).optional(),
  })
  .refine((r) => r.maxCapacity === undefined || r.maxCapacity >= r.minCapacity, {
    message: "maxCapacity muss größer oder gleich minCapacity sein",
  });

/** Shift within a volunteer event */
export const volunteerShiftSchema = z.object({
  id: z.uuid(),
  label: z.string().min(1).max(200),
  startDate: z.iso.datetime(),
  endDate: z.iso.datetime().optional(),
  archivedAt: z.iso
    .datetime()
    .optional()
    .describe("Set when the shift is archived; hides it from public signup form"),
  roles: z.array(volunteerRoleSchema),
});

/** EU kids (height cm) + adult unisex t-shirt sizes for volunteer signup */
export const VOLUNTEER_KIDS_T_SHIRT_SIZES = ["128", "140", "152", "164"] as const;
export const VOLUNTEER_ADULT_T_SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "3XL"] as const;
export const VOLUNTEER_T_SHIRT_SIZES = [
  ...VOLUNTEER_KIDS_T_SHIRT_SIZES,
  ...VOLUNTEER_ADULT_T_SHIRT_SIZES,
] as const;

export const volunteerTShirtSizeSchema = z.enum(VOLUNTEER_T_SHIRT_SIZES);

/** Volunteer event with nested shifts and roles */
export const volunteerEventSchema = z.object({
  ...baseEntityFields,
  type: z
    .literal("volunteerEvent")
    .default("volunteerEvent")
    .describe("Entity type, primary key for GSI queries"),
  title: z.string().min(1).max(200),
  description: z.string().optional(),
  location: z.string().optional(),
  locationUrl: z
    .string()
    .optional()
    .describe("Optional URL for the location (e.g. Google Maps link)"),
  organizerName: z
    .string()
    .min(1)
    .max(200)
    .describe("Name of the event organizer — shown in email contact line"),
  organizerEmail: z
    .email()
    .trim()
    .max(200)
    .describe("Email of the event organizer — set as Reply-To on all volunteer emails"),
  askForTShirtSize: z
    .boolean()
    .optional()
    .describe("When true, the public signup form requires a t-shirt size"),
  askForAssociation: z
    .boolean()
    .optional()
    .describe("When true, the public signup form requires Vereinszugehörigkeit"),
  shifts: z.array(volunteerShiftSchema),
  archivedAt: z.iso
    .datetime()
    .optional()
    .describe("Set when the event is archived; hides it from public view"),
});

/** Signup payload stored inside a verification token */
export const volunteerSignupDataSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.email().trim(),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD"),
  preferredRoleIds: z.array(z.uuid()),
  association: z
    .string()
    .trim()
    .max(500)
    .optional()
    .describe("Vereinszugehörigkeit — required when the event has askForAssociation enabled"),
  mobilePhone: z
    .string()
    .trim()
    .max(30)
    .optional()
    .describe("Optional mobile phone number of the volunteer"),
  emergencyContact: z
    .string()
    .trim()
    .max(30)
    .optional()
    .describe("Emergency contact phone number — required for minors (under 18 at shift start)"),
  note: z
    .string()
    .trim()
    .max(1000)
    .optional()
    .describe("Optional free-text note from the volunteer"),
  tShirtSize: volunteerTShirtSizeSchema
    .optional()
    .describe("T-shirt size — required when the event has askForTShirtSize enabled"),
  eventId: z.uuid(),
  shiftId: z.uuid(),
});

/** Volunteer signup record */
export const volunteerSignupSchema = volunteerSignupDataSchema.extend({
  ...baseEntityFields,
  type: z
    .literal("volunteerSignup")
    .default("volunteerSignup")
    .describe("Entity type discriminator"),
  status: z.enum(["pending", "confirmed", "canceled"]),
  assignedRoleId: z
    .uuid()
    .optional()
    .describe("Role assigned by admin, overrides helper preference"),
});

/** Short-lived verification token for confirming a volunteer signup */
export const volunteerTokenSchema = z.object({
  id: z.uuid(),
  type: z.literal("volunteerToken").default("volunteerToken").describe("Entity type discriminator"),
  eventId: z.uuid(),
  signupData: volunteerSignupDataSchema.describe("Full signup payload carried by the token"),
  ttl: z.number().int().positive().describe("Unix timestamp for DynamoDB TTL (72h expiry)"),
  createdAt: z.iso.datetime(),
});

export type VolunteerEventInput = z.infer<typeof volunteerEventSchema>;
export type VolunteerShiftInput = z.infer<typeof volunteerShiftSchema>;
export type VolunteerRoleInput = z.infer<typeof volunteerRoleSchema>;
export type VolunteerSignupInput = z.infer<typeof volunteerSignupSchema>;
export type VolunteerSignupData = z.infer<typeof volunteerSignupDataSchema>;
export type VolunteerTShirtSize = z.infer<typeof volunteerTShirtSizeSchema>;
export type VolunteerTokenInput = z.infer<typeof volunteerTokenSchema>;

/** Social-table ledger item for one Mastodon share of a concluded match UUID. */
export const MATCH_MASTODON_SHARE_SK = "mastodon-share";

export function matchMastodonSharePk(matchUuid: string): string {
  return `match#${matchUuid}`;
}

export const matchMastodonShareItemSchema = z.object({
  pk: z.string().min(1),
  sk: z.literal(MATCH_MASTODON_SHARE_SK),
  status: z.enum(["pending", "posted"]),
  createdAt: z.iso.datetime(),
  postedAt: z.iso.datetime().optional(),
  mastodonStatusId: z.string().optional(),
  ttl: z.number().int().positive(),
});

export type MatchMastodonShareItem = z.infer<typeof matchMastodonShareItemSchema>;
